// Music Manager Component for Club Penguin Game

interface MusicOptions {
  volume?: number; // 0.0 to 1.0
  loop?: boolean;
  fadeInDuration?: number; // milliseconds
  fadeOutDuration?: number; // milliseconds
}

export class MusicManager {
  private currentAudio: HTMLAudioElement | null = null;
  private currentTrack: string | null = null;
  private defaultVolume: number = 0.5;
  private fadeInterval: number | null = null;
  private userHasInteracted: boolean = false;
  private pendingMusic: { src: string; options: MusicOptions } | null = null;

  constructor(defaultVolume: number = 0.5) {
    this.defaultVolume = Math.max(0, Math.min(1, defaultVolume));
    this.setupUserInteractionHandler();
  }

  private setupUserInteractionHandler(): void {
    const enableAudio = () => {
      this.userHasInteracted = true;

      // Play pending music if any
      if (this.pendingMusic) {
        this.playMusic(this.pendingMusic.src, this.pendingMusic.options);
        this.pendingMusic = null;
      }

      // Remove listeners after first interaction
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('keydown', enableAudio);
      document.removeEventListener('touchstart', enableAudio);
    };

    // Listen for any user interaction
    document.addEventListener('click', enableAudio, { once: true });
    document.addEventListener('keydown', enableAudio, { once: true });
    document.addEventListener('touchstart', enableAudio, { once: true });
  }

  public async playMusic(src: string, options: MusicOptions = {}): Promise<void> {
    const {
      volume = this.defaultVolume,
      loop = true,
      fadeInDuration = 1000,
      fadeOutDuration = 500,
    } = options;

    // If same track is already playing, do nothing
    if (this.currentTrack === src && this.currentAudio && !this.currentAudio.paused) {
      return;
    }

    // If user hasn't interacted yet, store pending music
    if (!this.userHasInteracted) {
      this.pendingMusic = { src, options };
      console.log('Music queued - waiting for user interaction:', src);
      this.showMusicPrompt();
      return;
    }

    // Stop current music with fade out
    if (this.currentAudio) {
      await this.fadeOut(this.currentAudio, fadeOutDuration);
      this.currentAudio.pause();
      this.currentAudio = null;
    }

    // Create new audio element
    const audio = document.createElement('audio');
    audio.src = src;
    audio.loop = loop;
    audio.volume = 0; // Start at 0 for fade in

    try {
      // Load and play
      await new Promise((resolve, reject) => {
        audio.addEventListener('canplay', resolve, { once: true });
        audio.addEventListener('error', reject, { once: true });
        audio.load();
      });

      await audio.play();

      // Set current references
      this.currentAudio = audio;
      this.currentTrack = src;

      // Fade in
      await this.fadeIn(audio, volume, fadeInDuration);
    } catch (error) {
      console.warn('Failed to play music:', src, error);
    }
  }

  public async stopMusic(fadeOutDuration: number = 500): Promise<void> {
    if (!this.currentAudio) return;

    await this.fadeOut(this.currentAudio, fadeOutDuration);
    this.currentAudio.pause();
    this.currentAudio = null;
    this.currentTrack = null;
  }

  public pauseMusic(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
    }
  }

  public resumeMusic(): void {
    if (this.currentAudio) {
      this.currentAudio.play().catch(error => {
        console.warn('Failed to resume music:', error);
      });
    }
  }

  public setVolume(volume: number): void {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    this.defaultVolume = clampedVolume;

    if (this.currentAudio) {
      this.currentAudio.volume = clampedVolume;
    }
  }

  public getVolume(): number {
    return this.currentAudio ? this.currentAudio.volume : this.defaultVolume;
  }

  public isPlaying(): boolean {
    return this.currentAudio ? !this.currentAudio.paused : false;
  }

  public getCurrentTrack(): string | null {
    return this.currentTrack;
  }

  private fadeIn(audio: HTMLAudioElement, targetVolume: number, duration: number): Promise<void> {
    return new Promise(resolve => {
      if (duration <= 0) {
        audio.volume = targetVolume;
        resolve();
        return;
      }

      const startVolume = 0;
      const volumeStep = (targetVolume - startVolume) / (duration / 50);
      let currentVolume = startVolume;

      this.clearFadeInterval();
      this.fadeInterval = window.setInterval(() => {
        currentVolume += volumeStep;

        if (currentVolume >= targetVolume) {
          audio.volume = targetVolume;
          this.clearFadeInterval();
          resolve();
        } else {
          audio.volume = currentVolume;
        }
      }, 50);
    });
  }

  private fadeOut(audio: HTMLAudioElement, duration: number): Promise<void> {
    return new Promise(resolve => {
      if (duration <= 0) {
        audio.volume = 0;
        resolve();
        return;
      }

      const startVolume = audio.volume;
      const volumeStep = startVolume / (duration / 50);
      let currentVolume = startVolume;

      this.clearFadeInterval();
      this.fadeInterval = window.setInterval(() => {
        currentVolume -= volumeStep;

        if (currentVolume <= 0) {
          audio.volume = 0;
          this.clearFadeInterval();
          resolve();
        } else {
          audio.volume = currentVolume;
        }
      }, 50);
    });
  }

  private clearFadeInterval(): void {
    if (this.fadeInterval) {
      clearInterval(this.fadeInterval);
      this.fadeInterval = null;
    }
  }

  private showMusicPrompt(): void {
    // Only show once
    if (document.querySelector('.music-prompt')) return;

    const prompt = document.createElement('div');
    prompt.className = 'music-prompt';
    prompt.innerHTML = `
      <div class="music-prompt-content">
        🎵 Click anywhere to enable music
      </div>
    `;
    prompt.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 10px 15px;
      border-radius: 5px;
      font-size: 14px;
      z-index: 10000;
      pointer-events: none;
      animation: fade-in-out 4s ease-in-out forwards;
    `;

    // Add CSS animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fade-in-out {
        0% { opacity: 0; transform: translateY(-10px); }
        20% { opacity: 1; transform: translateY(0); }
        80% { opacity: 1; transform: translateY(0); }
        100% { opacity: 0; transform: translateY(-10px); }
      }
    `;
    document.head.appendChild(style);
    document.body.appendChild(prompt);

    // Remove after animation
    setTimeout(() => {
      if (prompt.parentNode) {
        prompt.parentNode.removeChild(prompt);
      }
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    }, 4000);
  }

  public destroy(): void {
    this.stopMusic(0);
    this.clearFadeInterval();

    // Clean up pending music
    this.pendingMusic = null;
  }
}
